import { Button, Card, Col, Drawer, InputNumber, List, Modal, Popconfirm, Row, Space, Spin, Tag, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, HistoryOutlined, MinusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemMonModal, { type MenuItem, type SelectedMenuItem } from '../../components/BanHang/ThemMonModal';
import TinhTienModal, { type HinhThucThanhToan } from '../../components/BanHang/TinhTienModal';
import ChuyenBanModal from '../../components/BanHang/ChuyenBanModal';
import BanModel from '../../models/ban';
import type ChiTietDatBanModel from '../../models/chi_tiet_dat_ban';
import TrangThaiBanModel from '../../models/trang_thai_ban';
import banService from '../../services/banService';
import hangHoaService from '../../services/hangHoaService';
import datBanService from '../../services/datBanService';
import qzTrayService from '../../services/qzTrayService';
import trangThaiBanService from '../../services/trangThaiBanService';

const { Title, Text } = Typography;
const TABLE_REFRESH_INTERVAL = 8000;

type TableOrder = {
  id: string;
  detailId: number | null;
  dishId: number;
  name: string;
  price: number;
  quantity: number;
  isCompleted: boolean;
  isDrink: boolean;
  soLuongSanPhamTrenDonVi?: number;
  ghi_chu?: string | null;
};

type TableItem = {
  id: number;
  name: string;
  status: 'trống' | 'đang phục vụ' | 'bàn khóa';
  total: number;
  orders: TableOrder[];
};

const formatMoney = (value: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);

const getDrinkQuantityLabel = (quantity: number, unitSize?: number) => {
  if (!unitSize || !Number.isFinite(unitSize) || unitSize <= 0) return null;

  const cases = Math.floor(quantity / unitSize);
  const cans = quantity % unitSize;
  return `${cases} Thùng + ${cans} lon`;
};

const mapOrderDetails = (details: ChiTietDatBanModel[], tableId: number): TableOrder[] =>
  details.map((detail, index) => ({
    id: detail.id != null ? String(detail.id) : `table-${tableId}-detail-${index}`,
    detailId: detail.id ?? null,
    dishId: detail.hang_hoa?.id ?? detail.hang_hoa_id ?? 0,
    name: detail.hang_hoa?.ten || 'Món ăn không còn tồn tại',
    price: detail.hang_hoa?.gia_ban_hien_thi ?? detail.hang_hoa?.gia_ban ?? 0,
    quantity: detail.so_luong,
    isCompleted: detail.trang_thai_mon_an?.key_value?.toUpperCase() === 'CHE_BIEN_XONG',
    isDrink: detail.hang_hoa?.nhom_hang_hoa?.key_value?.toUpperCase() === 'DO_UONG' || detail.hang_hoa?.nhom_hang_hoa?.key_value?.toUpperCase() === 'DO_UONG_THUNG_24_LON',
    soLuongSanPhamTrenDonVi:
      detail.hang_hoa?.nhom_hang_hoa?.key_value?.toUpperCase() === 'DO_UONG_THUNG_24_LON'
        ? detail.hang_hoa.nhom_hang_hoa.so_luong_san_pham_tren_don_vi
        : undefined,
    ghi_chu: detail.ghi_chu ?? null,
  }));

const getTableStatus = (status?: TrangThaiBanModel | null): TableItem['status'] => {
  const statusKey = status?.key_value?.toUpperCase();
  const statusName = status?.ten?.toLowerCase();

  if (statusKey === 'DANG_PHUC_VU' || statusName?.includes('đang phục vụ')) {
    return 'đang phục vụ';
  }

  if (statusKey === 'BAN_KHOA' || statusName?.includes('khóa')) {
    return 'bàn khóa';
  }

  return 'trống';
};

export default function BanHangPage() {
  const navigate = useNavigate();
  const employeeName = localStorage.getItem('user_name')?.trim();
  const [tables, setTables] = useState<TableItem[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [isMovingTable, setIsMovingTable] = useState(false);
  const [isReducingDish, setIsReducingDish] = useState(false);
  const [isUpdatingDishQuantity, setIsUpdatingDishQuantity] = useState(false);
  const [isRemovingDish, setIsRemovingDish] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isCheckingCheckoutStatus, setIsCheckingCheckoutStatus] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState<number>(0);
  const [selectedAddItems, setSelectedAddItems] = useState<SelectedMenuItem[]>([]);
  const [moveTargetId, setMoveTargetId] = useState<number | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [editingDrink, setEditingDrink] = useState<TableOrder | null>(null);
  const [editingDrinkQuantity, setEditingDrinkQuantity] = useState<number>(1);
  const [isMobileActionDrawerOpen, setIsMobileActionDrawerOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let isCurrent = true;
    let isLoadingTables = false;
    let tableStatuses: TrangThaiBanModel[] = [];

    const loadTables = async (loadSupportingData: boolean) => {
      if (isLoadingTables) return;
      isLoadingTables = true;

      try {
        if (loadSupportingData) setIsLoadingData(true);
        const bans = await banService.userSaleGetAllBan();
        let dishes: Awaited<ReturnType<typeof hangHoaService.userSaleGetAllHangHoa>> = [];
        if (loadSupportingData) {
          const [loadedStatuses, loadedDishes] = await Promise.all([
            trangThaiBanService.getAllTrangThaiBan(),
            hangHoaService.userSaleGetAllHangHoa(),
          ]);
          tableStatuses = loadedStatuses;
          dishes = loadedDishes;
        }
        if (!isCurrent) return;

        const loadedTables: TableItem[] = bans.map((ban: BanModel) => {
          const matchedStatus =
            tableStatuses.find((status) => status.id === ban.trang_thai_ban_id) ||
            tableStatuses.find(
              (status) =>
                status.key_value?.toUpperCase() === ban.trang_thai_ban?.key_value?.toUpperCase()
            ) ||
            ban.trang_thai_ban ||
            null;

          const orderDetails = (ban.dat_ban ?? [])
            .flatMap((datBan) => datBan.chi_tiet_dat_bans ?? []);
          const orders = mapOrderDetails(orderDetails, ban.id ?? 0);

          return {
            id: ban.id ?? 0,
            name: ban.ten,
            status: getTableStatus(matchedStatus),
            total: orders.reduce((sum, item) => sum + item.price * item.quantity, 0),
            orders,
          };
        });

        setTables(loadedTables);
        if (loadSupportingData) {
          const loadedMenuItems: MenuItem[] = dishes.map((dish) => ({
            id: dish.id ?? 0,
            name: dish.ten,
            price: dish.gia_ban_hien_thi ?? dish.gia_ban ?? 0,
            imageUrl: dish.hinh_anh_hang_hoas?.[0]?.full_path || dish.hinh_anh_hang_hoas?.[0]?.path || undefined,
            isDrink: dish.nhom_hang_hoa?.key_value?.toUpperCase() === 'DO_UONG' || dish.nhom_hang_hoa?.key_value?.toUpperCase() === 'DO_UONG_THUNG_24_LON',
            so_luong_san_pham_tren_don_vi:
              dish.nhom_hang_hoa?.key_value?.toUpperCase() === 'DO_UONG_THUNG_24_LON'
                ? dish.nhom_hang_hoa.so_luong_san_pham_tren_don_vi
                : undefined,
            so_luong_mac_dinh_hien_thi: dish.so_luong_mac_dinh_hien_thi,
            mac_dinh_them_vao_ban: dish.mac_dinh_them_vao_ban,
          }));
          setMenuItems(loadedMenuItems);
        }
        setSelectedTableId((currentTableId) =>
          loadedTables.some((table) => table.id === currentTableId)
            ? currentTableId
            : loadedTables[0]?.id ?? 0
        );
      } catch (error) {
        if (isCurrent) {
          message.error(error instanceof Error ? error.message : 'Không thể tải dữ liệu bàn và món ăn');
        }
      } finally {
        if (isCurrent) {
          if (loadSupportingData) setIsLoadingData(false);
        }
        isLoadingTables = false;
      }
    };

    void loadTables(true);
    const intervalId = window.setInterval(() => {
      void loadTables(false);
    }, TABLE_REFRESH_INTERVAL);

    return () => {
      isCurrent = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const selectedTable = useMemo(
    () => tables.find((table) => table.id === selectedTableId) || null,
    [tables, selectedTableId]
  );
  const selectedTableMenuQuantities = selectedTable?.orders.reduce<Array<{ id: number; quantity: number }>>(
    (quantities, order) => {
      const existing = quantities.find((item) => item.id === order.dishId);
      if (existing) {
        existing.quantity += order.quantity;
      } else {
        quantities.push({ id: order.dishId, quantity: order.quantity });
      }
      return quantities;
    },
    []
  ) ?? [];

  const availableTables = tables.filter(
    (table) => table.id !== selectedTableId && table.status !== 'bàn khóa'
  );

  const handleSelectTable = (tableId: number) => {
    setSelectedTableId(tableId);
    if (isMobileView) {
      setIsMobileActionDrawerOpen(true);
    }
  };

  const closeMobileActionDrawer = () => {
    setIsMobileActionDrawerOpen(false);
  };

  const handleAddDish = async () => {
    if (!selectedTable || selectedAddItems.length === 0) return;

    setIsSavingOrder(true);
    try {
      await datBanService.create({
        ban_id: selectedTable.id,
        chi_tiet_dat_ban: selectedAddItems.map((item) => ({
          hang_hoa_id: item.menuId,
          so_luong: item.quantity,
          ghi_chu: item.ghi_chu?.trim() || undefined,
        })),
      });

      setTables((prev) =>
        prev.map((table) => {
          if (table.id !== selectedTableId) return table;

          const defaultItems = selectedTable.status === 'trống'
            ? menuItems
                .filter((item) => item.mac_dinh_them_vao_ban)
                .filter((item) => !selectedAddItems.some((selectedItem) => selectedItem.menuId === item.id))
                .map((item) => ({
                  menuId: item.id,
                  quantity: item.so_luong_mac_dinh_hien_thi && item.so_luong_mac_dinh_hien_thi > 0
                    ? item.so_luong_mac_dinh_hien_thi
                    : 1,
                  ghi_chu: '',
                }))
            : [];
          const addedOrders = [...selectedAddItems, ...defaultItems].reduce<TableOrder[]>((orders, selectedItem) => {
            const chosenMenu = menuItems.find((item) => item.id === selectedItem.menuId);
            if (!chosenMenu) return orders;

            return [...orders, {
                id: `new-${Date.now()}-${orders.length}-${selectedItem.menuId}`,
              detailId: null,
              dishId: chosenMenu.id,
                name: chosenMenu.name,
                price: chosenMenu.price,
                quantity: selectedItem.quantity,
                isCompleted: false,
                isDrink: chosenMenu.isDrink ?? false,
                soLuongSanPhamTrenDonVi: chosenMenu.so_luong_san_pham_tren_don_vi,
                ghi_chu: selectedItem.ghi_chu?.trim() || null,
              }];
          }, table.orders);
          const nextOrders = addedOrders;

          return {
            ...table,
            status: 'đang phục vụ',
            orders: nextOrders,
            total: nextOrders.reduce((sum, item) => sum + item.price * item.quantity, 0),
          };
        })
      );

      setSelectedAddItems([]);
      setIsAddModalOpen(false);
      setIsMobileActionDrawerOpen(false);
      message.success(`Đã thêm ${selectedAddItems.length} món vào ${selectedTable.name}`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Không thể thêm món vào bàn');
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleRemoveDish = async (orderId: string) => {
    if (!selectedTable) return;

    const currentItem = selectedTable.orders.find((item) => item.id === orderId);
    if (!currentItem) return;
    if (currentItem.detailId == null) {
      message.error('Không xác định được món trong đơn đặt bàn');
      return;
    }

    setIsRemovingDish(true);
    try {
      const result = await datBanService.xoaMon(selectedTable.id, currentItem.detailId);
      setTables((prev) =>
        prev.map((table) => {
          if (table.id !== selectedTable.id) return table;

          const nextOrders = table.orders.filter((item) => item.id !== orderId);
          return {
            ...table,
            status: result.banTrong ? 'trống' : 'đang phục vụ',
            orders: nextOrders,
            total: nextOrders.reduce((sum, item) => sum + item.price * item.quantity, 0),
          };
        })
      );
      message.success(`Đã xóa ${currentItem.name} khỏi bàn`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Không thể xóa món khỏi bàn');
    } finally {
      setIsRemovingDish(false);
    }
  };

  const handleReduceDishQuantity = async (orderId: string) => {
    if (!selectedTable) return;

    const currentItem = selectedTable.orders.find((item) => item.id === orderId);
    if (!currentItem) return;
    if (currentItem.detailId == null) {
      message.error('Không xác định được món trong đơn đặt bàn');
      return;
    }

    setIsReducingDish(true);
    try {
      const result = await datBanService.giamMon(selectedTable.id, currentItem.detailId);
      setTables((prev) =>
        prev.map((table) => {
          if (table.id !== selectedTable.id) return table;

          const nextOrders = table.orders
            .map((item) => item.id === orderId ? { ...item, quantity: item.quantity - 1 } : item)
            .filter((item) => item.quantity > 0);

          return {
            ...table,
            status: result.banTrong ? 'trống' : 'đang phục vụ',
            orders: nextOrders,
            total: nextOrders.reduce((sum, item) => sum + item.price * item.quantity, 0),
          };
        })
      );

      message.success(
        currentItem.quantity > 1
          ? `Đã giảm ${currentItem.name} còn ${currentItem.quantity - 1}`
          : `Đã xóa ${currentItem.name} khỏi bàn`
      );
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Không thể giảm món');
    } finally {
      setIsReducingDish(false);
    }
  };

  const handleUpdateDrinkQuantity = async () => {
    if (!selectedTable || !editingDrink) return;
    if (editingDrink.detailId == null) {
      message.error('Không xác định được món trong đơn đặt bàn');
      return;
    }
    if (!Number.isInteger(editingDrinkQuantity) || editingDrinkQuantity < 1) {
      message.warning('Số lượng phải là số nguyên lớn hơn 0');
      return;
    }

    setIsUpdatingDishQuantity(true);
    try {
      const updatedQuantity = await datBanService.capNhatSoLuongMon(
        selectedTable.id,
        editingDrink.detailId,
        editingDrinkQuantity
      );
      setTables((previousTables) =>
        previousTables.map((table) => {
          if (table.id !== selectedTable.id) return table;
          const orders = table.orders.map((order) =>
            order.id === editingDrink.id ? { ...order, quantity: updatedQuantity } : order
          );
          return {
            ...table,
            orders,
            total: orders.reduce((sum, order) => sum + order.price * order.quantity, 0),
          };
        })
      );
      setEditingDrink(null);
      message.success(`Đã cập nhật ${editingDrink.name} thành ${updatedQuantity} phần`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Không thể cập nhật số lượng món');
    } finally {
      setIsUpdatingDishQuantity(false);
    }
  };

  const handleMoveTable = async () => {
    if (!selectedTable || moveTargetId === null) {
      message.warning('Vui lòng chọn bàn đích để chuyển bàn');
      return;
    }

    const targetTable = tables.find((table) => table.id === moveTargetId);
    if (!targetTable) {
      message.error('Không tìm thấy bàn đích');
      return;
    }

    setIsMovingTable(true);
    try {
      await datBanService.chuyenBan(selectedTable.id, targetTable.id);
      setTables((prev) => prev.map((table) => {
        if (table.id === selectedTable.id) {
          return { ...table, status: 'trống', total: 0, orders: [] };
        }
        if (table.id === targetTable.id) {
          const orders = [...table.orders, ...selectedTable.orders];

          return {
            ...table,
            status: 'đang phục vụ',
            orders,
            total: orders.reduce((sum, item) => sum + item.price * item.quantity, 0),
          };
        }
        return table;
      }));
      setSelectedTableId(targetTable.id);
      setIsMoveModalOpen(false);
      setIsMobileActionDrawerOpen(false);
      setMoveTargetId(null);
      message.success(`Đã chuyển ${selectedTable.name} sang ${targetTable.name}`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Không thể chuyển bàn');
    } finally {
      setIsMovingTable(false);
    }
  };

  const openCashDrawer = () => {
    void qzTrayService.openCashDrawer()
      .then(() => message.success('Đã gửi lệnh mở két tới máy in'))
      .catch((error: unknown) => {
        message.error(
          error instanceof Error
            ? `Không thể mở két qua QZ Tray: ${error.message}`
            : 'Không thể mở két qua QZ Tray'
        );
      });
  };

  const printReceipt = (cashReceived = 0) => {
    if (!selectedTable) return;

    const printWindow = window.open('', '_blank', 'width=420,height=700');
    if (!printWindow) {
      message.warning('Trình duyệt đã chặn cửa sổ in. Vui lòng cho phép popup và thử lại.');
      return;
    }

    const escapeHtml = (value: string) =>
      value.replace(/[&<>"']/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character] || character);
    const logoUrl = `${window.location.origin}${process.env.PUBLIC_URL}/image/logo-cuong-hoai.png`;
    const bankQrCodeUrl = `${window.location.origin}${process.env.PUBLIC_URL}/image/qrcode-ngan-hang.jpg`;
    const changeDue = Math.max(cashReceived - selectedTable.total, 0);
    const receiptItems = selectedTable.orders.reduce<Array<{
      dishId: number;
      name: string;
      price: number;
      quantity: number;
      subtotal: number;
      soLuongSanPhamTrenDonVi?: number;
    }>>((items, order) => {
      const existingItem = items.find((item) => item.dishId === order.dishId);
      if (existingItem) {
        existingItem.quantity += order.quantity;
        existingItem.subtotal += order.price * order.quantity;
      } else {
        items.push({
          dishId: order.dishId,
          name: order.name,
          price: order.price,
          quantity: order.quantity,
          subtotal: order.price * order.quantity,
          soLuongSanPhamTrenDonVi: order.soLuongSanPhamTrenDonVi,
        });
      }
      return items;
    }, []);

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Hóa đơn ${selectedTable.name}</title>
          <style>
            @page {
              size: 80mm auto;
              margin: 0;
            }

            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
              color: #000;
              font-family: 'Courier New', monospace;
              font-size: 13px;
              font-weight: 700;
              line-height: 1.45;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            body {
              width: 80mm;
              padding-top: 3mm;
              padding-right: 5mm;
              padding-bottom: 3mm;
              padding-left: 7mm;
              box-sizing: border-box;
            }

            .receipt {
              width: 100%;
              overflow-wrap: anywhere;
            }

            .title, .center {
              text-align: center;
              font-weight: 700;
            }

            .title {
              font-size: 16px;
            }

            .logo {
              display: block;
              max-width: 100%;
              max-height: 22mm;
              object-fit: contain;
              margin: 0 auto 3mm;
            }

            .bank-qr-code {
              display: block;
              width: 42mm;
              max-width: 100%;
              height: auto;
              margin: 3mm auto 0;
            }

            .divider {
              border-top: 1px dashed #000;
              margin: 8px 0;
            }

            .item {
              margin: 5px 0;
            }

            .item-name {
              font-weight: 700;
              overflow-wrap: anywhere;
            }

            .item-meta, .total {
              display: flex;
              justify-content: space-between;
              gap: 8px;
            }

            .total {
              font-size: 14px;
              font-weight: 700;
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <img class="logo" src="${logoUrl}" alt="Logo Làng Nướng Cương Hoài">
            <div class="title">LÀNG NƯỚNG CƯƠNG HOÀI</div>
            <div class="center">${escapeHtml(selectedTable.name)}</div>
            <div>${escapeHtml(new Date().toLocaleString('vi-VN'))}</div>
            <div class="divider"></div>
            ${receiptItems.map((item) => `
              <div class="item">
                <div class="item-name">${escapeHtml(item.name)}</div>
                <div class="item-meta">
                  <span>${item.quantity}${getDrinkQuantityLabel(item.quantity, item.soLuongSanPhamTrenDonVi)
                    ? ` (${getDrinkQuantityLabel(item.quantity, item.soLuongSanPhamTrenDonVi)})`
                    : ''} x ${formatMoney(item.price)}</span>
                  <span>${formatMoney(item.subtotal)}</span>
                </div>
              </div>
            `).join('')}
            <div class="divider"></div>
            <div class="total"><strong>Tổng tiền</strong><strong>${formatMoney(selectedTable.total)}</strong></div>
            <div class="item-meta"><span>Tiền khách đưa</span><span>${formatMoney(cashReceived)}</span></div>
            <div class="item-meta"><span>Tiền trả khách</span><span>${formatMoney(changeDue)}</span></div>
            <div class="center" style="margin-top: 8px">Quét mã QR để chuyển khoản</div>
            <img class="bank-qr-code" src="${bankQrCodeUrl}" alt="Mã QR thanh toán ngân hàng">
            <div class="center" style="margin-top: 14px">Cảm ơn quý khách!</div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleCheckout = async (paymentMethod: HinhThucThanhToan) => {
    if (!selectedTable) return;

    setIsCheckingOut(true);
    try {
      await datBanService.thanhToan(selectedTable.id, paymentMethod);
      const paidTable = selectedTable;

      setTables((prev) =>
        prev.map((table) =>
          table.id === paidTable.id
            ? { ...table, status: 'trống', total: 0, orders: [] }
            : table
        )
      );

      setIsCheckoutModalOpen(false);
      setIsMobileActionDrawerOpen(false);
      if (paymentMethod === 'TIEN_MAT') {
        openCashDrawer();
      }
      message.success(`Đã thanh toán ${paidTable.name}`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Không thể xác nhận thanh toán');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const openCheckout = async () => {
    if (!selectedTable || isCheckingCheckoutStatus) return;

    setIsCheckingCheckoutStatus(true);
    try {
      const latestTables = await banService.userSaleGetAllBan();
      const latestTable = latestTables.find((table) => table.id === selectedTable.id);
      if (!latestTable) {
        throw new Error('Không tìm thấy bàn để kiểm tra trạng thái món');
      }

      const latestOrderDetails = (latestTable.dat_ban ?? [])
        .flatMap((datBan) => datBan.chi_tiet_dat_bans ?? []);
      const latestOrders = mapOrderDetails(latestOrderDetails, latestTable.id);
      const unfinishedOrders = mapOrderDetails(
        latestOrderDetails.filter((detail) => detail.hang_hoa?.hien_thi_o_bep !== false),
        latestTable.id
      ).filter((order) => !order.isCompleted);

      setTables((previousTables) =>
        previousTables.map((table) =>
          table.id === latestTable.id
            ? {
                ...table,
                orders: latestOrders,
                total: latestOrders.reduce((sum, order) => sum + order.price * order.quantity, 0),
              }
            : table
        )
      );

      if (unfinishedOrders.length === 0) {
        setIsCheckoutModalOpen(true);
        return;
      }

      Modal.confirm({
        title: 'Một số món chưa chế biến xong',
        content: (
          <>
            <p>Danh sách món chưa hoàn tất tại {selectedTable.name}:</p>
            <List
              size="small"
              bordered
              dataSource={unfinishedOrders}
              renderItem={(order) => (
                <List.Item>
                  <Space>
                    <Text strong>{order.name}</Text>
                    <Text type="secondary">Số lượng: {order.quantity}</Text>
                  </Space>
                </List.Item>
              )}
            />
            <p style={{ margin: '12px 0 0' }}>Bạn vẫn muốn tiếp tục tính tiền?</p>
          </>
        ),
        okText: 'Tiếp tục tính tiền',
        cancelText: 'Quay lại',
        onOk: () => setIsCheckoutModalOpen(true),
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Không thể kiểm tra trạng thái chế biến món');
    } finally {
      setIsCheckingCheckoutStatus(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_type_key');
    window.location.href = '/login';
  };

  return (
    <div className="banhang-page">
      <div className="banhang-header">
        <div className="banhang-header-main">
          <Title level={2} style={{ margin: 0 }}>LÀNG NƯỚNG CƯƠNG HOÀI</Title>
          <Text type="secondary">Quản lý đơn hàng và bàn phục vụ</Text>
        </div>
        <div className="banhang-header-actions">
          <Space wrap>
            <Text strong>Nhân viên: {employeeName}</Text>
            <Button icon={<HistoryOutlined />} onClick={() => navigate('/lich-su-ban-hang')}>
              Lịch sử bán hàng
            </Button>
            <Button onClick={handleLogout}>Đăng xuất</Button>
          </Space>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Card className="table-panel">
            <Title level={4}>Danh sách bàn</Title>
            {isLoadingData ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <Spin tip="Đang tải danh sách bàn và món ăn..." />
              </div>
            ) : (
              <Row gutter={[12, 12]}>
                {tables.map((table) => (
                <Col xs={12} sm={8} md={6} key={table.id}>
                  <button
                    type="button"
                    className={`table-card ${selectedTableId === table.id ? 'selected' : ''} ${table.status === 'trống' ? 'empty' : ''} ${table.status === 'đang phục vụ' ? 'serving' : ''} ${table.status === 'bàn khóa' ? 'locked' : ''}`}
                    onClick={() => handleSelectTable(table.id)}
                  >
                    <div className="table-card-title">{table.name}</div>
                    <div className="table-card-status">
                      <Tag color={table.status === 'trống' ? 'green' : table.status === 'bàn khóa' ? 'red' : 'orange'}>
                        {table.status}
                      </Tag>
                    </div>
                    <div className="table-card-total">{formatMoney(table.total)}</div>
                  </button>
                  </Col>
                ))}
              </Row>
            )}
          </Card>
        </Col>

        {!isMobileView && (
          <Col xs={24} lg={9}>
            {selectedTable ? (
              <Card className="detail-panel">
                <div className="detail-header">
                  <Title level={4} style={{ margin: 0 }}>{selectedTable.name}</Title>
                  <Tag color={selectedTable.status === 'trống' ? 'green' : selectedTable.status === 'bàn khóa' ? 'red' : 'orange'}>
                    {selectedTable.status}
                  </Tag>
                </div>

                <div className="action-box">
                  <Title level={5}>Thao tác</Title>
                  <div className="detail-actions">
                    <Button type="primary" onClick={() => setIsAddModalOpen(true)} disabled={selectedTable.status === 'bàn khóa'}>Thêm món</Button>
                    <Button onClick={() => setIsMoveModalOpen(true)} disabled={selectedTable.status !== 'đang phục vụ'}>
                      Chuyển bàn
                    </Button>
                    <Button
                      type="primary"
                      danger
                      onClick={() => void openCheckout()}
                      loading={isCheckingCheckoutStatus}
                      disabled={selectedTable.status !== 'đang phục vụ'}
                    >
                      Tính tiền
                    </Button>
                  </div>
                </div>

                <div className="order-box">
                  <Title level={5}>Danh sách món</Title>
                  <List
                    dataSource={selectedTable.orders}
                    locale={{ emptyText: 'Bàn chưa có món nào' }}
                    renderItem={(item) => (
                      <List.Item>
                        <div className="order-item">
                          <div>
                            <Text strong>{item.name}</Text>
                            <div>{formatMoney(item.price)} x {item.quantity}</div>
                            {item.ghi_chu && (
                              <div><Text type="secondary">Ghi chú: {item.ghi_chu}</Text></div>
                            )}
                          </div>
                          <Space size={4}>
                            <Text>{formatMoney(item.price * item.quantity)}</Text>
                            {item.isDrink && (
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                title={`Thay đổi số lượng ${item.name}`}
                                aria-label={`Thay đổi số lượng ${item.name}`}
                                onClick={() => {
                                  setEditingDrink(item);
                                  setEditingDrinkQuantity(item.quantity);
                                }}
                              />
                            )}
                            <Popconfirm
                              title={item.quantity > 1
                                ? `Giảm ${item.name} còn ${item.quantity - 1} phần?`
                                : `${item.name} chỉ còn 1 phần. Giảm sẽ xóa món khỏi bàn. Tiếp tục?`}
                              okText="Giảm"
                              cancelText="Hủy"
                              onConfirm={() => handleReduceDishQuantity(item.id)}
                            >
                              <Button
                                type="text"
                                icon={<MinusOutlined />}
                                title={`Giảm số lượng ${item.name}`}
                                aria-label={`Giảm số lượng ${item.name}`}
                                style={{ marginLeft: 10 }}
                                disabled={
                                  isReducingDish ||
                                  isRemovingDish ||
                                  isUpdatingDishQuantity ||
                                  item.detailId == null
                                }
                              />
                            </Popconfirm>
                            <Popconfirm
                              title={`Xóa ${item.name} khỏi ${selectedTable.name}?`}
                              okText="Xóa"
                              cancelText="Hủy"
                              onConfirm={() => handleRemoveDish(item.id)}
                            >
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                aria-label={`Xóa ${item.name}`}
                              />
                            </Popconfirm>
                          </Space>
                        </div>
                      </List.Item>
                    )}
                  />

                  <div className="order-summary">
                    <Text strong>Tổng tiền:</Text>
                    <Text strong style={{ fontSize: 18 }}>{formatMoney(selectedTable.total)}</Text>
                  </div>
                </div>
              </Card>
            ) : null}
          </Col>
        )}
      </Row>

      <Drawer
        title={`Bàn ${selectedTable?.name || ''}`}
        open={isMobileView && isMobileActionDrawerOpen && Boolean(selectedTable)}
        placement="bottom"
        height={420}
        onClose={closeMobileActionDrawer}
        closable
      >
        {selectedTable && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>Trạng thái:</Text>
              <Tag color={selectedTable.status === 'trống' ? 'green' : selectedTable.status === 'bàn khóa' ? 'red' : 'orange'}>
                {selectedTable.status}
              </Tag>
            </div>

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Button
                type="primary"
                block
                onClick={() => {
                  setIsMobileActionDrawerOpen(false);
                  setIsAddModalOpen(true);
                }}
                disabled={selectedTable.status === 'bàn khóa'}
              >
                Thêm món
              </Button>
              <Button
                block
                onClick={() => {
                  setIsMobileActionDrawerOpen(false);
                  setIsMoveModalOpen(true);
                }}
                disabled={selectedTable.status !== 'đang phục vụ'}
              >
                Chuyển bàn
              </Button>
            </Space>

            <div>
              <Text strong>Danh sách món</Text>
              {selectedTable.orders.length > 0 ? (
                <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
                  {selectedTable.orders.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 8,
                        padding: '8px 10px',
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        background: '#fafafa',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{item.name}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {formatMoney(item.price)} x {item.quantity}
                          {getDrinkQuantityLabel(item.quantity, item.soLuongSanPhamTrenDonVi)
                            ? ` (${getDrinkQuantityLabel(item.quantity, item.soLuongSanPhamTrenDonVi)})`
                            : ''}
                        </div>
                        {item.ghi_chu && (
                          <div style={{ fontSize: 12, color: '#fa8c16', marginTop: 4 }}>
                            Ghi chú: {item.ghi_chu}
                          </div>
                        )}
                      </div>
                      <Text strong>{formatMoney(item.price * item.quantity)}</Text>
                      {item.isDrink && (
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          title={`Thay đổi số lượng ${item.name}`}
                          aria-label={`Thay đổi số lượng ${item.name}`}
                          onClick={() => {
                            setEditingDrink(item);
                            setEditingDrinkQuantity(item.quantity);
                          }}
                        />
                      )}
                    </div>
                  ))}
                </Space>
              ) : (
                <div style={{ marginTop: 8, color: '#999' }}>Bàn chưa có món nào</div>
              )}
            </div>

            <div style={{ paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Tổng tiền:</Text>
                <Text strong style={{ fontSize: 18 }}>{formatMoney(selectedTable.total)}</Text>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <ThemMonModal
        open={isAddModalOpen}
        selectedTableName={selectedTable?.name}
        confirmLoading={isSavingOrder}
        menuItems={menuItems}
        currentOrders={selectedTableMenuQuantities}
        selectedItems={selectedAddItems}
        onChange={setSelectedAddItems}
        onConfirm={handleAddDish}
        onCancel={() => {
          setSelectedAddItems([]);
          setIsAddModalOpen(false);
        }}
      />

      <Modal
        title={`Thay đổi số lượng: ${editingDrink?.name || ''}`}
        open={editingDrink !== null}
        confirmLoading={isUpdatingDishQuantity}
        okText="Lưu"
        cancelText="Hủy"
        onOk={() => void handleUpdateDrinkQuantity()}
        onCancel={() => setEditingDrink(null)}
      >
        <InputNumber
          aria-label="Số lượng đồ uống"
          min={1}
          precision={0}
          value={editingDrinkQuantity}
          onChange={(value) => setEditingDrinkQuantity(typeof value === 'number' ? value : 1)}
          style={{ width: '100%' }}
        />
      </Modal>

      <ChuyenBanModal
        open={isMoveModalOpen}
        selectedTableName={selectedTable?.name}
        availableTables={availableTables}
        moveTargetId={moveTargetId}
        confirmLoading={isMovingTable}
        onSelectTarget={setMoveTargetId}
        onConfirm={handleMoveTable}
        onCancel={() => {
          setIsMoveModalOpen(false);
          setMoveTargetId(null);
        }}
      />

      <TinhTienModal
        open={isCheckoutModalOpen}
        selectedTableName={selectedTable?.name}
        total={selectedTable?.total ?? 0}
        confirmLoading={isCheckingOut}
        onConfirm={handleCheckout}
        onPrint={(cashReceived) => printReceipt(cashReceived)}
        onCancel={() => setIsCheckoutModalOpen(false)}
      />
    </div>
  );
}
